import { QueryKeys } from "@/constants/Query";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import {
  type TaskListResponse,
  isUnsolicitedLab,
} from "../network/useTaskList";
import type { TaskListCountResponse } from "./useTaskListCount";

export const useMarkUnsolicitedResolved = ({
  patientId,
  diagnosticReportId,
  onError,
}: {
  patientId: string;
  diagnosticReportId: string;
  onError?: () => void;
}) => {
  const queryClient = useQueryClient();

  return useApiMutation<void, void>({
    mutationOptions: {
      mutationKey:
        QueryKeys.Patient.Unmatched.MARK_UNSOLICITED_DX_REPORT_RESOLVED(
          patientId,
          diagnosticReportId,
        ),
      mutationFn: () => {
        return axiosInstance.put(
          `/labs/results/unsolicited/${diagnosticReportId}/resolve`,
        );
      },
      onSuccess: () => {
        // Task list APIs are pretty slow at higher numbers of tasks, so just
        // remove the task from the cache directly.
        queryClient.setQueryData<TaskListResponse>(
          QueryKeys.Provider.TASK_LIST,
          (oldData) => {
            return {
              ...oldData,
              taskListItems:
                oldData?.taskListItems.filter((task) => {
                  if (
                    isUnsolicitedLab(task) &&
                    task.diagnosticReportId === diagnosticReportId
                  ) {
                    return false;
                  }
                  return true;
                }) || [],
            };
          },
        );
        queryClient.setQueryData<TaskListCountResponse>(
          QueryKeys.Provider.TASK_LIST_COUNT,
          (oldData) => {
            if (!oldData) {
              return oldData;
            }
            return {
              ...oldData,
              labUnsolicitedTasksCount: oldData?.labUnsolicitedTasksCount - 1,
              totalTaskCount: oldData?.totalTaskCount - 1,
            };
          },
        );
      },
      onError,
    },
  });
};
